import { useState } from "react";
import { ImageOff, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { StatusBadge } from "@/components/site/StatusBadge";
import { errorMessage, imgSrc } from "@/lib/api";
import { ConfirmButton } from "./ConfirmButton";

const StockInput = ({ p, onSave }) => {
  const [v, setV] = useState(String(p.stock_quantity));
  const commit = () => {
    const n = Number(v);
    if (!Number.isInteger(n) || n < 0) { setV(String(p.stock_quantity)); toast.error("Stock must be a whole number, 0 or more"); return; }
    if (n !== p.stock_quantity) onSave({ stock_quantity: n });
  };
  return (
    <input type="number" min="0" inputMode="numeric" value={v} onChange={(e) => setV(e.target.value)} onBlur={commit}
           onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} aria-label={`Stock for ${p.name}`}
           className="h-10 w-20 rounded-lg border border-[#E5E0D8] px-2 text-center outline-none focus:border-[#C59B27]" data-testid={`stock-input-${p.id}`} />
  );
};

const Row = ({ p, onEdit, onPatch, onDelete }) => (
  <tr className="border-b border-[#E5E0D8] last:border-0 hover:bg-[#FDFBF7]" data-testid={`inventory-row-${p.id}`}>
    <td className="p-3">
      <div className="flex items-center gap-3">
        <span className="grid h-12 w-12 shrink-0 place-items-center overflow-hidden rounded-lg bg-[#F4EEE2]">
          {p.images?.[0] ? <img src={imgSrc(p.images[0].url)} alt="" loading="lazy" className="h-full w-full object-cover" /> : <ImageOff className="h-4 w-4 text-[#8B5A2B]/60" />}
        </span>
        <span className="min-w-0">
          <span className="block truncate font-semibold text-[#1F2421]">{p.name}</span>
          <span className="text-xs text-[#5B625D]">{p.sku || "No SKU"}{p.is_demo && <span className="ml-2 rounded bg-[#10291F] px-1.5 py-0.5 text-[10px] font-bold text-[#F3DB8C]">DEMO</span>}</span>
        </span>
      </div>
    </td>
    <td className="p-3 text-sm">{p.category}</td>
    <td className="p-3"><StockInput key={p.stock_quantity} p={p} onSave={(b) => onPatch(p, b)} /></td>
    <td className="p-3"><StatusBadge status={p.availability_status} testId={`inventory-status-${p.id}`} /></td>
    <td className="p-3 text-sm">{p.selling_price != null ? `₹${p.selling_price}` : "—"}</td>
    <td className="p-3"><Switch checked={p.is_published} onCheckedChange={(v) => onPatch(p, { is_published: v })} data-testid={`publish-switch-${p.id}`} aria-label="Published" /></td>
    <td className="p-3">
      <div className="flex gap-1">
        <button onClick={() => onEdit(p)} className="grid h-10 w-10 place-items-center rounded-lg hover:bg-[#F4EEE2]" aria-label="Edit" data-testid={`edit-product-${p.id}`}><Pencil className="h-4 w-4" /></button>
        <ConfirmButton testId={`delete-product-${p.id}`} title="Delete this product?" description={`"${p.name}" and its photos will be removed from the catalogue. This cannot be undone.`}
                       onConfirm={() => onDelete(p)} className="grid h-10 w-10 place-items-center rounded-lg text-[#8A2B1E] hover:bg-[#F6E4E1]">
          <Trash2 className="h-4 w-4" />
        </ConfirmButton>
      </div>
    </td>
  </tr>
);

export const InventoryTable = ({ items, loading, onEdit, onPatch, onDelete }) => {
  const patch = async (p, body) => {
    try { await onPatch(p.id, body); toast.success(`Saved "${p.name}"`); } catch (e) { toast.error(errorMessage(e)); }
  };
  const del = async (p) => {
    try { await onDelete(p.id); toast.success(`Deleted "${p.name}"`); } catch (e) { toast.error(errorMessage(e)); }
  };
  return (
    <div className="overflow-x-auto rounded-2xl border border-[#E5E0D8] bg-white" data-testid="inventory-table">
      <table className="w-full min-w-[820px] text-left">
        <thead className="bg-[#F4EEE2] text-xs uppercase tracking-wider text-[#5B625D]">
          <tr><th className="p-3">Product</th><th className="p-3">Category</th><th className="p-3">Stock</th><th className="p-3">Status</th><th className="p-3">Price (private)</th><th className="p-3">Published</th><th className="p-3">Actions</th></tr>
        </thead>
        <tbody>
          {loading && <tr><td colSpan={7} className="p-8 text-center text-[#5B625D]">Loading inventory…</td></tr>}
          {!loading && !items.length && <tr><td colSpan={7} className="p-8 text-center text-[#5B625D]" data-testid="inventory-empty">No products match these filters.</td></tr>}
          {items.map((p) => <Row key={p.id} p={p} onEdit={onEdit} onPatch={patch} onDelete={del} />)}
        </tbody>
      </table>
    </div>
  );
};
